/**
 * EditCircuitNotesDialog — captures a markdown body for one of two
 * fields on a :Circuit:
 *
 *   * `description` — long-lived intent (what is this candidate for,
 *     what's it exploring).
 *   * `release_notes_md` — captured at the moment a circuit
 *     transitions to RELEASED.
 *
 * Both fields share the same lightweight textarea UI, so the dialog
 * is parameterised by `field` rather than split into two components.
 *
 * The dialog is "dumb": parent owns the patch mutation. When the user
 * hits Save, `onSave(value)` fires with the trimmed value (empty
 * string when cleared). For the `release_notes_md + release` flow
 * the parent should chain the patch so a single PATCH call carries
 * both `status: "released"` and the release notes.
 *
 * State lives on the `EditCircuitNotesDialogBody` sub-component which
 * only mounts when `open` is true. This pattern (carried over from the
 * 2026-05-17 audit's A-003 sweep on MappingBench / RenameTheatreDialog)
 * lets the textarea reseed on every open with zero `useEffect` glue:
 * the body remounts fresh, so `useState(initialValue ?? "")` IS the
 * reseed.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";

type Field = "description" | "release_notes_md";

interface EditCircuitNotesDialogProps {
  open: boolean;
  /** Which field is being edited. Drives the title, placeholder, and
   *  save-button label. */
  field: Field;
  /** Pre-fill — the field's current value. Null is treated as empty. */
  initialValue: string | null;
  /** Circuit name for the title. */
  circuitName: string;
  /** When supplied, the save button reads as "Save & release" — used
   *  in the release flow where the dialog gates the status transition. */
  saveAsRelease?: boolean;
  onSave: (value: string) => void;
  onClose: () => void;
}

const COPY = {
  description: {
    title: (n: string) => `Description — ${n}`,
    helper:
      "Why does this circuit exist alongside main? What trade-offs is it exploring? Markdown is supported.",
    placeholder:
      "e.g. 'Candidate for the GaN switching node — explores 5×R_DSon reduction at the cost of EMI work.'",
    saveLabel: "Save",
    rows: 6,
  },
  release_notes_md: {
    title: (n: string) => `Release notes — ${n}`,
    helper:
      "Optional. What's shipping in this release? Captured on the circuit, alongside the version chain.",
    placeholder:
      "e.g. 'v1.2 — final BOM after the supply-chain swap. R5 changed to 1206 footprint.'",
    saveLabel: "Save",
    rows: 8,
  },
} as const;

export function EditCircuitNotesDialog(props: EditCircuitNotesDialogProps) {
  // Body only mounts while `open` is true. Closing unmounts; the next
  // open mounts fresh and the body's `useState(initialValue ?? "")`
  // re-initialises from the latest prop. No reset useEffect needed.
  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      {props.open && <EditCircuitNotesDialogBody {...props} />}
    </Dialog>
  );
}

function EditCircuitNotesDialogBody({
  field,
  initialValue,
  circuitName,
  saveAsRelease,
  onSave,
  onClose,
}: EditCircuitNotesDialogProps) {
  const copy = COPY[field];
  const [value, setValue] = useState<string>(initialValue ?? "");

  return (
    <>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {copy.title(circuitName)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <DialogContentText sx={{ fontSize: 13 }}>
            {copy.helper}
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            rows={copy.rows}
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={copy.placeholder}
            variant="outlined"
            sx={(t) => ({
              "& .MuiInputBase-input": {
                fontFamily: t.fonts.mono,
                fontSize: 13,
                lineHeight: 1.55,
              },
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(value.trim());
            onClose();
          }}
        >
          {saveAsRelease ? "Save & release" : copy.saveLabel}
        </Button>
      </DialogActions>
    </>
  );
}
